import { useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry, themeQuartz, type ColDef, type GridApi } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import type { RecordRow } from '../../contracts';
import { GridLoading } from '../../shared/ui';
import { getGridLocaleText, getLocale, useI18n } from '../../shared/i18n';
import { useGridLanguage } from '../../shared/useGridLanguage';

ModuleRegistry.registerModules([AllCommunityModule]);
const warehouseTheme = themeQuartz.withParams({ fontSize: 12, rowHeight: 28, headerHeight: 30, spacing: 4 });
const defaultColumns = { sortable: true, resizable: true, filter: true };
const selection = { mode: 'singleRow' as const, enableClickSelection: true, checkboxes: false };
export const text = (row: RecordRow, field: string) => String(row[field] ?? '');
export const number = (row: RecordRow, field: string) => Number(row[field] ?? 0);
export const format = (value: unknown, digits = 2) => value == null ? '' : Number(value).toLocaleString(getLocale(), { maximumFractionDigits: digits, minimumFractionDigits: digits });
export const col = (field: string, headerName: string, width = 120, numeric = false): ColDef<RecordRow> => ({
  field, headerName, width, ...(numeric ? { type: 'numericColumn', valueFormatter: p => format(p.value) } : {}),
});

export function WarehouseGrid({ rows, columns, onSelect, onActivate, rowKey = 'ID', loading = false, loadingLabel = 'Daten werden geladen …' }: {
  rows: RecordRow[]; columns: ColDef<RecordRow>[]; onSelect?: (row: RecordRow) => void;
  onActivate?: (row: RecordRow) => void; rowKey?: string;
  loading?: boolean; loadingLabel?: string;
}) {
  const { t } = useI18n();
  const grid = useRef<GridApi<RecordRow> | null>(null);
  useGridLanguage(grid);
  const localizedColumns = useMemo(() => columns.map(({ width, ...column }) => ({ ...column, initialWidth: width, headerName: t(column.headerName ?? '') })), [columns, t]);
  return <div className="warehouse-grid" aria-busy={loading}><AgGridReact<RecordRow>
    theme={warehouseTheme}
    onGridReady={event => { grid.current = event.api; }}
    getLocaleText={getGridLocaleText}
    rowData={rows} columnDefs={localizedColumns} defaultColDef={defaultColumns} maintainColumnOrder
    loading={loading} loadingOverlayComponent={GridLoading} loadingOverlayComponentParams={{ label: loadingLabel }}
    getRowId={p => text(p.data, rowKey)} rowSelection={selection}
    onRowClicked={e => { if (!loading && e.data) onSelect?.(e.data); }}
    onRowDoubleClicked={e => { if (!loading && e.data) onActivate?.(e.data); }}
    onCellKeyDown={e => { if (!loading && (e.event as KeyboardEvent)?.key === 'Enter' && e.data) onActivate?.(e.data); }}
    onSelectionChanged={e => { const row = e.api.getSelectedRows()[0]; if (!loading && row) onSelect?.(row); }}
    overlayNoRowsTemplate={t('Keine Einträge.')} />
  </div>;
}
